export type SchedulerRequirement = "Required" | "Recommended" | "Optional" | "Not Needed";
export type SchedulerStatus = "Not Needed" | "Not Sent" | "Sent" | "Scheduled" | "Completed" | "Reschedule Needed" | "Canceled";

export interface SchedulerTypeDefinition {
  id: string;
  label: string;
  defaultDurationMinutes: number;
  description: string;
}

export interface SchedulerRuleInput {
  selectedPackage?: string;
  selectedServices?: string[];
  packageRules?: Record<string, SchedulerRequirement>;
  serviceRules?: Record<string, SchedulerRequirement>;
  enabled?: boolean;
}

export const schedulerRequirementOptions: SchedulerRequirement[] = ["Required", "Recommended", "Optional", "Not Needed"];
export const schedulerStatusOptions: SchedulerStatus[] = ["Not Needed", "Not Sent", "Sent", "Scheduled", "Completed", "Reschedule Needed", "Canceled"];

export const schedulerTypes: SchedulerTypeDefinition[] = [
  { id: "discovery-call", label: "Discovery Call", defaultDurationMinutes: 30, description: "Initial fit, goals, needs, and service recommendation conversation." },
  { id: "proposal-review-call", label: "Proposal Review Call", defaultDurationMinutes: 30, description: "Client review of proposal scope, pricing, payment terms, and next steps." },
  { id: "agreement-questions-call", label: "Agreement / Project Questions Call", defaultDurationMinutes: 30, description: "A focused call for agreement questions or project clarification before work begins." },
  { id: "kickoff-call", label: "Kickoff Call", defaultDurationMinutes: 60, description: "Project launch call for details, timelines, content needs, design direction, expectations, and next steps." },
  { id: "revision-review-call", label: "Revision Review Call", defaultDurationMinutes: 30, description: "Review feedback, revision requests, and remaining client approvals." },
  { id: "final-delivery-review-call", label: "Final Delivery Review Call", defaultDurationMinutes: 30, description: "Final walkthrough before delivery, launch, or completion." },
];

export const defaultPackageSchedulerRules: Record<string, Record<string, SchedulerRequirement>> = {
  "essential-experience": { "kickoff-call": "Optional" },
  "signature-experience": { "kickoff-call": "Recommended" },
  "legacy-experience": { "kickoff-call": "Required" },
  "legacy-collection": { "kickoff-call": "Required" },
  "custom-quote": { "discovery-call": "Required", "kickoff-call": "Required", "final-delivery-review-call": "Recommended" },
};

export const defaultServiceSchedulerRules: Record<string, Record<string, SchedulerRequirement>> = {
  "custom-community-experience": { "discovery-call": "Required", "kickoff-call": "Required", "final-delivery-review-call": "Recommended" },
};

const requirementWeight: Record<SchedulerRequirement, number> = {
  "Not Needed": 0,
  Optional: 1,
  Recommended: 2,
  Required: 3,
};

export function schedulerTypeById(id: string) {
  return schedulerTypes.find((type) => type.id === id);
}

export function strongestRequirement(values: SchedulerRequirement[]) {
  return values.reduce<SchedulerRequirement>((best, value) => (requirementWeight[value] > requirementWeight[best] ? value : best), "Not Needed");
}

export function defaultSchedulerRequirementForType(schedulerType: string, selectedPackage?: string, selectedServices: string[] = []) {
  const packageRequirement = selectedPackage ? defaultPackageSchedulerRules[selectedPackage]?.[schedulerType] : undefined;
  const serviceRequirements = selectedServices.map((serviceId) => defaultServiceSchedulerRules[serviceId]?.[schedulerType]).filter(Boolean) as SchedulerRequirement[];
  return strongestRequirement([packageRequirement || "Not Needed", ...serviceRequirements]);
}

export function schedulerRequirementForSelection(schedulerType: string, input: SchedulerRuleInput) {
  if (input.enabled === false) return "Not Needed";
  const packageRequirement = input.selectedPackage
    ? input.packageRules?.[input.selectedPackage] || defaultPackageSchedulerRules[input.selectedPackage]?.[schedulerType]
    : undefined;
  const serviceRequirements = (input.selectedServices || [])
    .map((serviceId) => input.serviceRules?.[serviceId] || defaultServiceSchedulerRules[serviceId]?.[schedulerType])
    .filter(Boolean) as SchedulerRequirement[];
  return strongestRequirement([packageRequirement || "Not Needed", ...serviceRequirements]);
}

export function defaultSchedulerStatus(requirement: SchedulerRequirement): SchedulerStatus {
  return requirement === "Not Needed" ? "Not Needed" : "Not Sent";
}

export function isProductionBlockedByKickoff(requirement: SchedulerRequirement, status: SchedulerStatus, manualOverride?: boolean) {
  return requirement === "Required" && status !== "Completed" && !manualOverride;
}

